import {
  DEFAULT_POOL_RISK_MOTION,
  mergePoolRiskMotionDefaults,
  type PoolRiskMotionDefaults,
} from './poolRiskMotion';
import {
  DETECTION_LEVEL_IDS,
  DETECTION_LEVEL_META,
  type DetectionLevelId,
} from './detectionPresets';

export type MotionRiskPresetId = DetectionLevelId | 'custom';

const ANCHOR_SENSITIVE: PoolRiskMotionDefaults = mergePoolRiskMotionDefaults({
  sensitivity: 0.82,
  stillness_threshold_sec: 6,
  combined_score_threshold: 22,
  legacy_detection_threshold: 2.6,
  subtype_min_score: {
    drowning: 0.26,
    no_motion: 0.3,
    sinking: 0.24,
  },
});

const ANCHOR_BALANCED: PoolRiskMotionDefaults = mergePoolRiskMotionDefaults(DEFAULT_POOL_RISK_MOTION);

const ANCHOR_CONSERVATIVE: PoolRiskMotionDefaults = mergePoolRiskMotionDefaults({
  sensitivity: 0.62,
  stillness_threshold_sec: 11,
  combined_score_threshold: 34,
  legacy_detection_threshold: 4.4,
  subtype_min_score: {
    drowning: 0.4,
    no_motion: 0.46,
    sinking: 0.36,
  },
});

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

function lerpMotion(
  a: PoolRiskMotionDefaults,
  b: PoolRiskMotionDefaults,
  t: number
): PoolRiskMotionDefaults {
  return mergePoolRiskMotionDefaults({
    sensitivity: Number(lerp(a.sensitivity, b.sensitivity, t).toFixed(3)),
    stillness_threshold_sec: Math.round(
      lerp(a.stillness_threshold_sec, b.stillness_threshold_sec, t)
    ),
    combined_score_threshold: Math.round(
      lerp(a.combined_score_threshold, b.combined_score_threshold, t)
    ),
    legacy_detection_threshold: Number(
      lerp(a.legacy_detection_threshold, b.legacy_detection_threshold, t).toFixed(2)
    ),
    subtype_min_score: {
      drowning: Number(
        lerp(a.subtype_min_score.drowning, b.subtype_min_score.drowning, t).toFixed(2)
      ),
      no_motion: Number(
        lerp(a.subtype_min_score.no_motion, b.subtype_min_score.no_motion, t).toFixed(2)
      ),
      sinking: Number(
        lerp(a.subtype_min_score.sinking, b.subtype_min_score.sinking, t).toFixed(2)
      ),
    },
  });
}

export function motionRiskAtLevel(level: DetectionLevelId): PoolRiskMotionDefaults {
  const n = Number(level);
  if (n <= 5) {
    const t = (n - 1) / 4;
    return lerpMotion(ANCHOR_SENSITIVE, ANCHOR_BALANCED, t);
  }
  const t = (n - 5) / 3;
  return lerpMotion(ANCHOR_BALANCED, ANCHOR_CONSERVATIVE, t);
}

export const MOTION_RISK_PRESETS: Record<DetectionLevelId, PoolRiskMotionDefaults> =
  Object.fromEntries(DETECTION_LEVEL_IDS.map((id) => [id, motionRiskAtLevel(id)])) as Record<
    DetectionLevelId,
    PoolRiskMotionDefaults
  >;

export const MOTION_LEVEL_META: Record<
  DetectionLevelId,
  { title: string; motionDescription: string; motionCctvDescription: string }
> =
  Object.fromEntries(
    DETECTION_LEVEL_IDS.map((id) => [
      id,
      {
        title: DETECTION_LEVEL_META[id].title,
        motionDescription:
          id === '1'
            ? '위험 신호를 가장 쉽게 냄. 오탐·경보가 느기 가능.'
            : id === '5'
              ? '시스템 기본 motion 값. 대부분 여기서 시작'
              : id === '8'
                ? '위험 판정을 가장 엄격하게. 놓침이 느기 가능'
                : Number(id) < 5
                  ? '균형보다 위험을 더 잘 잡는 쪽'
                  : '균형보다 오탐·경보를 줄이는 쪽',
        motionCctvDescription:
          id === '5'
            ? '이 카메라 민감도·정지를 시설 5단계와 동일하게 맞춤. 통합·유형 임계는 시설 값을 따름.'
            : '이 카메라만 민감도·정지를 단계에 맞게 설정. 통합·유형 임계는 시설 Motion 설정을 따름.',
      },
    ])
  ) as Record<
    DetectionLevelId,
    { title: string; motionDescription: string; motionCctvDescription: string }
  >;

const TOL = 0.025;

function near(a: number, b: number, tol = TOL): boolean {
  return Math.abs(a - b) <= tol;
}

function motionMatchesPreset(a: PoolRiskMotionDefaults, b: PoolRiskMotionDefaults): boolean {
  return (
    near(a.sensitivity, b.sensitivity) &&
    a.stillness_threshold_sec === b.stillness_threshold_sec &&
    a.combined_score_threshold === b.combined_score_threshold &&
    near(a.legacy_detection_threshold, b.legacy_detection_threshold, 0.06) &&
    near(a.subtype_min_score.drowning, b.subtype_min_score.drowning) &&
    near(a.subtype_min_score.no_motion, b.subtype_min_score.no_motion) &&
    near(a.subtype_min_score.sinking, b.subtype_min_score.sinking)
  );
}

export function inferMotionRiskPreset(
  motion: PoolRiskMotionDefaults | Partial<PoolRiskMotionDefaults> | null | undefined
): MotionRiskPresetId {
  const m = mergePoolRiskMotionDefaults(motion);
  for (const id of DETECTION_LEVEL_IDS) {
    if (motionMatchesPreset(m, MOTION_RISK_PRESETS[id])) {
      return id;
    }
  }
  return 'custom';
}

export function applyMotionRiskPreset(level: DetectionLevelId): PoolRiskMotionDefaults {
  return { ...MOTION_RISK_PRESETS[level] };
}

/** CCTV 전용 오버라이드(민감도·정지) — 8단계는 시설 motion 프리셋과 동일 축 */
export type CctvMotionOverrideFields = {
  risk_detection_sensitivity: number;
  risk_detection_stillness_sec: number;
};

export function applyCctvMotionOverridePreset(level: DetectionLevelId): CctvMotionOverrideFields {
  const p = MOTION_RISK_PRESETS[level];
  return {
    risk_detection_sensitivity: p.sensitivity,
    risk_detection_stillness_sec: p.stillness_threshold_sec,
  };
}

export function inferCctvMotionOverridePreset(
  sensitivity: number | string | null | undefined,
  stillnessSec: number | string | null | undefined
): MotionRiskPresetId {
  const s = Number(sensitivity);
  const st = Number(stillnessSec);
  if (!Number.isFinite(s) || !Number.isFinite(st)) {
    return 'custom';
  }
  for (const id of DETECTION_LEVEL_IDS) {
    const p = MOTION_RISK_PRESETS[id];
    if (near(s, p.sensitivity) && st === p.stillness_threshold_sec) {
      return id;
    }
  }
  return 'custom';
}
